<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useTranslate } from '@tolgee/vue'
import { useRoute } from 'vue-router'

import type { IReceipt, ImpactType, IReceiptItem, TransactionPurchase } from '@/api/interfaces'
import { usePublicData } from '@/stores/PublicData'
import { useEditorStore } from '@/stores/Editor'
import type { IReceiptTransferred } from '@/stores/interfaces/PublicData'
import { IMPACT_TYPES } from '@/api/interfaces'
import { formatDateToMonthYear } from '@/helpers/utils'

import PublicLedgerReceipt from '@/components/public-page/PublicLedgerReceipt.vue'
import BackgroundIcon from '@/components/icons/backgrounds/BackgroundIcon.vue'

interface Props {
  purchases: TransactionPurchase[]
  transferredReceipts?: IReceiptTransferred[]
}

const props = defineProps<Props>()

const { t } = useTranslate()
const publicStore = usePublicData()
const editorStore = useEditorStore()
const tab = ref<ImpactType>('trees')
const accordion = ref([])
const route = useRoute()
const totalReceipts = ref<Record<ImpactType, number>>({
  trees: 5,
  carbon: 5,
  plastic: 5,
  kelp: 5,
  water: 5,
  bees: 5,
})

watch(tab, () => {
  accordion.value = []
})

const allReceipts = computed<IReceipt[]>(() => {
  const impactTypes = publicStore.impacts
    ?.filter((item) => item.source === 'account')
    .map((impact) => impact.type)

  const receiptsMap: { [date: string]: { [type: string]: IReceiptItem[] } } = {}

  props.purchases.forEach((purchase) => {
    if (!receiptsMap[purchase.periodEndDate]) {
      receiptsMap[purchase.periodEndDate] = {}
    }

    if (!receiptsMap[purchase.periodEndDate][purchase.type]) {
      receiptsMap[purchase.periodEndDate][purchase.type] = []
    }

    receiptsMap[purchase.periodEndDate][purchase.type].push({
      id: purchase.id,
      name: t.value(`${purchase.projectId}.name`),
      url: purchase.receiptUrl,
    })
  })

  const receipts: IReceipt[] = []

  for (const date in receiptsMap) {
    for (const type in receiptsMap[date]) {
      receipts.push({
        date: formatDateToMonthYear(date),
        type: type as ImpactType,
        time: new Date(date).getTime(),
        receipts: receiptsMap[date][type],
      })
    }
  }

  const accountSignupDate = new Date(publicStore.signupDate).getTime()
  const sortedReceipts = receipts.sort((a, b) => b.time - a.time)
  const filteredReceipts = sortedReceipts
    .filter((receipt) => impactTypes?.includes(receipt.type) && receipt.time > accountSignupDate)
    .filter((receipt) => editorStore.editor.icon.impactDisplay[receipt.type])

  return route.name === 'CommunityPage' ? sortedReceipts : filteredReceipts
})

const getReceiptsByType = (type: ImpactType): IReceipt[] => {
  return allReceipts.value.filter((receipt) => receipt?.type?.toLowerCase() === type)
}

const receiptList = (type: ImpactType) => {
  return getReceiptsByType(type).slice(0, totalReceipts.value[type])
}

const loadMore = (type: ImpactType) => {
  const remainingReceipts = getReceiptsByType(type).length - totalReceipts.value[type]
  if (remainingReceipts > 0) {
    totalReceipts.value[type] += Math.min(5, remainingReceipts)
  }
}

const getTabs = (): ImpactType[] => {
  return IMPACT_TYPES.filter((type) => getReceiptsByType(type).length > 0)
}

const transformReceipt = (item: IReceiptTransferred): IReceiptItem => {
  return {
    name: t.value(item.type),
    url: item.url,
  }
}
</script>

<template>
  <section id="ledger" class="section">
    <BackgroundIcon
      class="background-icon"
      align="right"
      :icon-type="editorStore.editor.background.icon"
    />
    <v-container>
      <div class="section-header">
        <h2 class="section-title">{{ $t('PublicLedger.title') }}</h2>
        <p class="section-description">{{ $t('PublicLedger.description') }}</p>
      </div>
      <div v-if="allReceipts.length > 0" class="public-ledger-wrapper">
        <v-tabs v-model="tab" align-tabs="center">
          <v-tab v-for="(type, index) in getTabs()" :key="index" :value="type">
            {{ $t(type) }}
          </v-tab>
          <v-tab v-if="!!transferredReceipts && transferredReceipts.length > 0">
            {{ $t('PublicLedger.transferredImpact.label') }}
          </v-tab>
        </v-tabs>

        <v-window v-if="purchases.length > 0" v-model="tab">
          <v-window-item v-for="(type, index) in getTabs()" :value="type" :key="index">
            <v-expansion-panels :model-value="accordion" variant="accordion">
              <v-expansion-panel
                v-for="(receipt, key) in receiptList(type)"
                :key="key"
                elevation="0"
              >
                <v-expansion-panel-title>{{ receipt.date }}</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <PublicLedgerReceipt
                    v-for="(item, index) in receipt.receipts"
                    :key="index"
                    :receipt="item"
                    variant="compact"
                  />
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            <button
              v-if="getReceiptsByType(type).length > totalReceipts[type]"
              @click="loadMore(type)"
              class="link load-more mt-2"
            >
              {{ $t('PublicLedger.load_more') }}
            </button>
          </v-window-item>
          <v-window-item v-if="!!transferredReceipts && transferredReceipts.length > 0">
            <div class="receipt-list">
              <p class="receipt-list-title">
                {{ $t('PublicLedger.transferredImpact.title', { name: publicStore.name }) }}
              </p>
              <PublicLedgerReceipt
                v-for="(item, index) in transferredReceipts"
                :key="index"
                :receipt="transformReceipt(item)"
              />
            </div>
          </v-window-item>
        </v-window>
      </div>
      <div class="mt-2 text-center">
        <RouterLink
          v-if="$route.name === 'PublicPage'"
          :to="{ name: 'CommunityPage', hash: '#ledger' }"
          class="link"
        >
          {{ $t('PublicLedger.all') }}
        </RouterLink>
      </div>
    </v-container>
  </section>
</template>

<style scoped lang="scss">
.section {
  position: relative;

  .community-page & {
    margin-bottom: 0;
    padding-bottom: 130px;
  }

  :deep(.background-icon) {
    display: none;
    position: absolute;

    .community-page & {
      display: none;
    }

    @include media('md-and-up') {
      display: block;
      top: 50%;
      right: -35px;
      transform: translateY(-50%);
      z-index: 0;
      width: 215px;
      height: 175px;
    }
  }
}

.public-ledger-wrapper {
  position: relative;
  z-index: 1;
  border-radius: 20px;
  margin-top: 60px;
  padding: 64px 16px 40px;
  background-color: var(--ui-emerald-green);

  @include media('md-and-up') {
    padding: 64px 104px;
  }

  .dark & {
    background-color: var(--gray-light-db);
  }

  .custom & {
    background-color: rgba(var(--custom-theme-color-rgb), 0.8);
  }

  .community-page & {
    background-color: rgba(33, 33, 33, 0.5);
    backdrop-filter: blur(10px);
  }

  .load-more {
    color: var(--ui-black);
    font-size: 20px;
    font-weight: 400;
    margin: 24px auto 0;

    .dark & {
      color: var(--ui-black);
    }

    .custom & {
      color: var(--lightest);
    }
  }
}

.receipt-list {
  margin-top: 40px;
  gap: 8px;
  display: flex;
  flex-direction: column;
}

.receipt-list-title {
  font-size: 18px;
  text-align: center;
  margin-bottom: 16px;

  @include media('md-and-up') {
    font-size: 24px;
  }
}

.v-tabs {
  margin-top: -85px;

  @include media('xs-only') {
    height: auto;
  }

  :deep(.v-slide-group__content) {
    gap: 8px;

    @include media('xs-only') {
      flex-direction: column;
    }

    @include media('md-and-up') {
      gap: 24px;
    }
  }

  .v-tab {
    background-color: #a8a8a8;
    border-radius: 20px !important;
    color: var(--ui-white);
    padding: 0 24px;

    @include media('xs-only') {
      width: 100%;
    }

    :deep(.v-btn__content) {
      font-weight: bold;
      font-size: 18px;
      text-transform: none;
      letter-spacing: -0.36px;
    }

    &:hover {
      background-color: var(--ui-green);

      .dark & {
        background-color: var(--darkest);
      }

      .custom & {
        background-color: var(--custom-theme-color);
      }

      .community-page & {
        background-color: var(--darkest);
      }
    }

    &.v-tab--selected {
      background-color: var(--ui-green);

      .dark & {
        background-color: var(--darkest);
      }

      .custom & {
        background-color: var(--custom-theme-color);
      }

      .community-page & {
        background-color: var(--darkest);
      }

      :deep(.v-tab__slider) {
        opacity: 0;
      }
    }
  }
}

.v-expansion-panels {
  gap: 8px;
  margin-top: 40px;

  .v-expansion-panel-title {
    &:hover {
      :deep(.v-expansion-panel-title__overlay) {
        opacity: 0 !important;
      }
    }

    :deep(.v-expansion-panel-title__overlay) {
      opacity: 0 !important;
    }
  }
}
</style>
